import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { BillyService } from 'src/app/@pages/billyService/billy.service';
import { IQuota } from 'src/app/@interfaces/quotation.interface';
import {
  BillyPayer,
  IBillyInvoice,
  IBillyToGraphQ,
  IItemsInvoice,
} from 'src/app/@interfaces/Billy/billy.interface';
import { DialogComponent } from '../dialog/dialog.component';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '@shared-services';

@Injectable({ providedIn: 'root' })
export class BillyInvoiceService {
  private spinnerService = inject(NgxSpinnerService);
  private translate = inject(TranslateService);
  private quotationService = inject(QuotationsService);
  private dialog = inject(MatDialog);
  private billyService = inject(BillyService);
  private alertService = inject(AlertService);

  private sendingInProgress = false;
  private sentInvoices = new Set<number>();

  async sendDian(invoiceId: number): Promise<boolean | void> {
    this.spinnerService.show();
    const invoiceObject: IQuota = await firstValueFrom(
      this.quotationService.getQuotation(invoiceId),
    );
    const billyInvoice = invoiceObject.billyInvoice;
    if (!billyInvoice) return;

    if (this.sentInvoices.has(invoiceId)) {
      this.spinnerService.hide();
      this.showWarning('This invoice is already being sent...');
      return;
    }

    if (this.sendingInProgress) {
      this.spinnerService.hide();
      this.showWarning(
        'A sending process is already in progress, please wait.',
      );
      return;
    }

    this.sendingInProgress = true;
    this.sentInvoices.add(invoiceId);

    try {
      if (billyInvoice.invoice_number !== 'PENDING') {
        await this.handleAlreadySentInvoice(billyInvoice.id_billy);
        return;
      }
      this.spinnerService.hide();
      const confirm = await new Promise<boolean>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('global.alerts.title.info'),
            message: this.translate.instant(
              'You are about to send this invoice to DIAN, are you sure?.',
            ),
            type: 'info',
            acceptText: 'Ok',
            rejectText: this.translate.instant('Abort'),
          })
          .subscribe({
            accept: () => resolve(true),
            reject: () => resolve(false),
            close: () => resolve(false),
          });
      });

      if (!confirm) return;

      const currency = await firstValueFrom(
        this.dialog
          .open(DialogComponent, { data: { currency: true } })
          .afterClosed(),
      );

      if (!currency) {
        this.showError('No currency selected.');
        return;
      }

      this.spinnerService.show();

      const payerId = await firstValueFrom(
        this.billyService.getPayer(invoiceObject.cliente.doc_nit),
      ).then((p: BillyPayer) => p.data?.[0]?.id ?? '');

      const billyInvoiceData = this.setInvoiceBilly(
        invoiceObject,
        payerId,
        currency,
      );

      const billyResponse = await firstValueFrom(
        this.billyService.postInvoice(billyInvoiceData),
      );

      return await this.saveBillyResponse(billyResponse, invoiceId);
    } catch (error) {
      this.billyErrorHandler(error as HttpErrorResponse);
    } finally {
      this.sendingInProgress = false;
      this.sentInvoices.delete(invoiceId);
      this.spinnerService.hide();
    }
  }

  private async handleAlreadySentInvoice(billyId: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.billyService.getInvoice(billyId));
      const { status, pdfUrl } = data.data.attributes;
      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.info'),
        message: `${this.translate.instant('This invoice has already been sent')}, ${this.translate.instant('status')}: 
          ${this.translate.instant(status)}
          <a href=${pdfUrl} target="_blank">${this.translate.instant('View invoice')}</a>`,
        type: 'info',
        acceptText: 'Ok',
      });
    } catch {
      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.info'),
        message: this.translate.instant(
          'This invoice does not have an id associated with the electronic supplier, please refer to the number on your supplier`s website.',
        ),
        type: 'info',
        acceptText: 'Ok',
      });
    }
  }

  private async saveBillyResponse(
    billyResponse: IBillyToGraphQ,
    invoiceId: number,
  ): Promise<boolean> {
    const { data } = billyResponse;
    const { attributes } = data;

    const result = await firstValueFrom(
      this.quotationService.setBillyInvoice(
        invoiceId.toString(),
        data.id ?? 'TRIED TO SEND',
        attributes.invoiceNumber ?? 'PENDING',
        new Date().toISOString(),
      ),
    );

    if (result) {
      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.success'),
        message: `${this.translate.instant('Invoice sent to Billy with id')}: ${attributes.invoiceNumber}`,
        type: 'success',
        acceptText: 'Ok',
      });
      return true;
    }
    return false;
  }

  private setInvoiceBilly(
    quotationObject: IQuota,
    payerId: string,
    currencies: string,
  ): IBillyInvoice {
    const items: IItemsInvoice[] = quotationObject.venta.map((item) => {
      const article: IArticles = item.articulo[0];
      const price = this.calculatePrice(
        item.precio,
        item.descuento,
        article.tax![0].value,
        quotationObject.tax_incl!,
      );
      return {
        type: 'item',
        id: article.codigo!,
        attributes: { quantity: item.cantidad, unitaryValue: price },
      };
    });

    const due = new Date(quotationObject.dueDate);
    const expiration = `${due.getDate()}/${due.getMonth() + 1}/${due.getFullYear()}`;

    return {
      data: {
        type: 'invoice',
        attributes: {
          credit: true,
          currency: currencies,
          expiration,
          items,
          payerId: payerId,
          usePayerAddress: true,
          purchaseOrder: '',
          observations: quotationObject.observations ?? '',
        },
      },
    };
  }

  private calculatePrice(
    price: number,
    discount: number,
    taxValue: number,
    taxIncluded: string,
  ): number {
    const incl = taxIncluded === 'true';
    let finalPrice = price;

    if (incl) {
      const basePrice = price / (taxValue + 1);
      const baseDiscount = discount ? (discount / 100) * basePrice : 0;
      finalPrice = basePrice - baseDiscount;
    } else if (discount) {
      finalPrice = price - (discount / 100) * price;
    }

    return finalPrice;
  }

  private showWarning(message: string): void {
    this.alertService.open({
      title: this.translate.instant('global.alerts.title.warning'),
      message: this.translate.instant(message),
      type: 'warning',
      acceptText: 'Ok',
    });
  }

  private showError(message: string): void {
    this.alertService.open({
      title: this.translate.instant('global.alerts.title.error'),
      message: this.translate.instant(message),
      type: 'error',
      acceptText: 'Ok',
    });
  }

  private billyErrorHandler(error: HttpErrorResponse): void {
    this.spinnerService.hide();
    this.billyService.handleError(error);
  }
}
